import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Polyline, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Globe, Package } from "lucide-react";

const ORIGIN = { name: "Kingston, Jamaica", lat: 17.9714, lng: -76.7931 };

// Approximate coordinates for the countries SHOP NOW serves.
const COORDS = {
  "Jamaica": [17.97, -76.79], "United States": [39.5, -98.35], "Canada": [56.13, -106.35],
  "United Kingdom": [54.0, -2.0], "Trinidad & Tobago": [10.69, -61.22], "Bahamas": [25.03, -77.4],
  "Barbados": [13.19, -59.54], "Guyana": [4.86, -58.93], "Haiti": [18.97, -72.29], "Cuba": [21.52, -77.78],
  "Dominican Republic": [18.74, -70.16], "Grenada": [12.12, -61.68], "Saint Lucia": [13.9, -60.98],
  "Cayman Islands": [19.31, -81.25], "Antigua & Barbuda": [17.06, -61.8], "Belize": [17.19, -88.5],
  "Panama": [8.54, -80.78], "Costa Rica": [9.75, -83.75], "Mexico": [23.63, -102.55], "Brazil": [-14.24, -51.93],
  "Colombia": [4.57, -74.3], "Venezuela": [6.42, -66.59], "Argentina": [-38.42, -63.62], "Chile": [-35.68, -71.54],
  "Peru": [-9.19, -75.02], "Spain": [40.46, -3.75], "France": [46.6, 2.21], "Germany": [51.17, 10.45],
  "Italy": [41.87, 12.57], "Netherlands": [52.13, 5.29], "Belgium": [50.5, 4.47], "Portugal": [39.4, -8.22],
  "Switzerland": [46.82, 8.23], "Ireland": [53.41, -8.24], "Sweden": [60.13, 18.64], "Norway": [60.47, 8.47],
  "Denmark": [56.26, 9.5], "Poland": [51.92, 19.15], "Russia": [61.52, 105.32], "Greece": [39.07, 21.82],
  "Turkey": [38.96, 35.24], "Israel": [31.05, 34.85], "United Arab Emirates": [23.42, 53.85],
  "Saudi Arabia": [23.89, 45.08], "Egypt": [26.82, 30.8], "Nigeria": [9.08, 8.68], "Ghana": [7.95, -1.02],
  "Kenya": [-0.02, 37.91], "South Africa": [-30.56, 22.94], "Morocco": [31.79, -7.09], "India": [20.59, 78.96],
  "Pakistan": [30.38, 69.35], "China": [35.86, 104.2], "Japan": [36.2, 138.25], "South Korea": [35.91, 127.77],
  "Philippines": [12.88, 121.77], "Vietnam": [14.06, 108.28], "Thailand": [15.87, 100.99],
  "Indonesia": [-0.79, 113.92], "Malaysia": [4.21, 101.98], "Singapore": [1.35, 103.82],
  "Australia": [-25.27, 133.78], "New Zealand": [-40.9, 174.89],
};

const HUB_ICON = L.divIcon({ className: "", html: '<span class="sn-hub-pulse"></span>', iconSize: [26, 26], iconAnchor: [13, 13] });
const ORDER_ICON = L.divIcon({ className: "", html: '<span class="sn-order-pulse"></span>', iconSize: [18, 18], iconAnchor: [9, 9] });
const BURST_ICON = L.divIcon({ className: "", html: '<span class="sn-order-burst"></span>', iconSize: [30, 30], iconAnchor: [15, 15] });

export default function DeliveryWorldMap() {
  const { user } = useAuth();
  const [dests, setDests] = useState([]);
  const [bursts, setBursts] = useState([]); // recent "new order" events -> animated for a few seconds
  const prev = useRef(null);
  const seq = useRef(0);
  const adminRef = useRef(false);
  const audioRef = useRef(null);

  useEffect(() => { adminRef.current = !!(user && user.role === "admin"); }, [user]);

  // Soft "cha-ching" chime (admin only) when a new order lands — synthesised, no asset needed.
  function playChime() {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioRef.current) audioRef.current = new AC();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t0 = ctx.currentTime;
      [[988, t0], [1319, t0 + 0.11]].forEach(([freq, t]) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
        o.start(t); o.stop(t + 0.4);
      });
    } catch { /* autoplay blocked */ }
  }

  useEffect(() => {
    let alive = true;
    async function poll() {
      try {
        const { data } = await api.get("/delivery-map");
        if (!alive) return;
        const list = data.destinations || [];
        setDests(list);
        const counts = Object.fromEntries(list.map((d) => [d.country, d.count]));
        if (prev.current) {
          for (const [country, n] of Object.entries(counts)) {
            const before = prev.current[country] || 0;
            if (n > before && COORDS[country]) {
              const id = ++seq.current;
              setBursts((b) => [...b, { id, country, coords: COORDS[country] }]);
              setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 6000);
              if (adminRef.current) playChime();
            }
          }
        }
        prev.current = counts;
      } catch { /* offline */ }
    }
    poll();
    const t = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const orderMap = Object.fromEntries(dests.map((d) => [d.country, d.count]));
  const served = Object.keys(COORDS);
  const activeCountries = served.filter((c) => orderMap[c] > 0);

  return (
    <section className="bg-ink text-white sn-noise" data-testid="delivery-world-map">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <span className="inline-flex items-center gap-2 text-emerald text-xs font-semibold uppercase tracking-wider">
              <Globe size={14} /> Worldwide Delivery
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-3">From Kingston to the World</h2>
            <p className="text-white/60 text-sm mt-2 max-w-lg">Live delivery lanes from our Jamaica hub to {served.length}+ countries. Red lanes show where recent orders are heading right now.</p>
          </div>
          <div className="flex gap-6" data-testid="delivery-map-stats">
            <div>
              <div className="text-3xl font-bold text-emerald">{served.length}+</div>
              <div className="text-xs text-white/50 uppercase tracking-wide">Countries served</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white flex items-center gap-1"><Package size={22} className="text-emerald" />{dests.reduce((a, d) => a + d.count, 0)}</div>
              <div className="text-xs text-white/50 uppercase tracking-wide">Orders shipped</div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl" style={{ height: 480 }} data-no-translate>
          <MapContainer center={[20, 0]} zoom={2} minZoom={2} scrollWheelZoom={false} worldCopyJump
            className="sn-dark-map" style={{ height: "100%", width: "100%", background: "#0b0d10" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Origin hub */}
            <CircleMarker center={[ORIGIN.lat, ORIGIN.lng]} radius={9}
              pathOptions={{ color: "#ffffff", weight: 2, fillColor: "#00A651", fillOpacity: 1 }}>
              <Tooltip direction="top">SHOP NOW Hub — {ORIGIN.name}</Tooltip>
            </CircleMarker>
            <Marker position={[ORIGIN.lat, ORIGIN.lng]} icon={HUB_ICON} interactive={false} />

            {/* Continuous pulse on countries that have live orders */}
            {Object.keys(orderMap).filter((c) => orderMap[c] > 0 && COORDS[c]).map((c) => (
              <Marker key={"pulse-" + c} position={COORDS[c]} icon={ORDER_ICON} interactive={false} />
            ))}

            {/* Burst animation when a brand-new order lands */}
            {bursts.map((b) => (
              <Marker key={"burst-" + b.id} position={b.coords} icon={BURST_ICON} interactive={false} zIndexOffset={1000}>
                <Tooltip permanent direction="top" className="sn-order-tip">New order → {b.country}</Tooltip>
              </Marker>
            ))}

            {/* Served countries */}
            {served.map((c) => {
              const [lat, lng] = COORDS[c];
              const orders = orderMap[c] || 0;
              const active = orders > 0;
              return (
                <CircleMarker key={c} center={[lat, lng]} radius={active ? Math.min(6 + orders, 16) : 4}
                  pathOptions={{
                    color: active ? "#E11B22" : "#00A651",
                    weight: active ? 2 : 1,
                    fillColor: active ? "#E11B22" : "#00A651",
                    fillOpacity: active ? 0.85 : 0.35,
                  }}>
                  <Tooltip direction="top">{c}{active ? ` — ${orders} order${orders > 1 ? "s" : ""}` : " · delivery available"}</Tooltip>
                </CircleMarker>
              );
            })}

            {/* Active order lanes from Kingston */}
            {activeCountries.map((c) => (
              <Polyline key={"line-" + c} positions={[[ORIGIN.lat, ORIGIN.lng], COORDS[c]]}
                pathOptions={{ color: "#E11B22", weight: 1.5, opacity: 0.55, dashArray: "4 6" }} />
            ))}
          </MapContainer>
        </div>
        <div className="flex items-center gap-6 mt-4 text-xs text-white/50">
          <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald inline-block" /> Delivery available</span>
          <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-[#E11B22] inline-block" /> Recent orders</span>
        </div>
      </div>
    </section>
  );
}
